import {
  Box,
  IconButton,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import {
  DeleteOutlined,
  Download,
  VisibilityOutlined,
} from "@mui/icons-material";
import { useDispatchWrapper } from "../../../hooks";
import {
  closeLoaderWithMessage,
  openLoaderWithMessage,
  showMessage,
} from "../../../redux/messages/messagesActions";
import { downLoadFile, getFileNameFromUrl, previewFile } from "../utils";
import { ViewImageDialog } from "../../FileUploadIcon/ViewImageDialog";
import {
  getFileExtensionType,
  IFileExtension,
} from "../../FileUploadIcon/helpers";
import { downloadSingleFileAsync } from "../../../redux/common/helpers";
import { fileIconMap } from "./fileIcons";

export const SingleFileDisplay: React.FC<{
  file?: File | null;
  value: string | null;
  onDelete?: () => void;
  deleteDisabled?: boolean;
  openPreviewInNewTab?: boolean;
}> = (props) => {
  const {
    file,
    value,
    onDelete,
    deleteDisabled,
    openPreviewInNewTab = false,
  } = props;
  const dispatch = useDispatchWrapper();
  const [loading, setLoading] = React.useState(false);
  const [openFilePreview, setOpenFilePreview] = React.useState<string | null>(
    null,
  );

  const extensionType = getFileExtensionType(value || "");

  const handleDownloadFile = async () => {
    if (file) {
      const downloadLink = URL.createObjectURL(file);
      const a = document.createElement("a");
      a.href = downloadLink;
      a.download = file.name || "download";
      a.click();
      URL.revokeObjectURL(downloadLink);
      return;
    }
    if (!loading) {
      try {
        setLoading(true);
        await downLoadFile(props.value);
      } catch (err) {
        dispatch(
          showMessage({
            displayAs: "snackbar",
            message: "Someting went to be wrong!",
            type: "error",
          }),
        );
      } finally {
        setLoading(false);
      }
    }
  };

  const [imageDialog, setImageDialog] = useState<{
    view: boolean;
    data: string;
    type: IFileExtension;
  }>({ view: false, data: "", type: "OTHER" });

  const handlePreviewFile = async () => {
    if (file) {
      const downloadLink = URL.createObjectURL(file);

      setOpenFilePreview(downloadLink);
      window.open(downloadLink, "_blank");
      return;
    } else {
      const fileUrl = props.value;

      if (!fileUrl) {
        dispatch(
          showMessage({
            type: "error",
            message: "No file available to view",
            displayAs: "snackbar",
          }),
        );
        return;
      }

      const fileType = getFileExtensionType(fileUrl);

      if (fileType === "EXCEL") {
        setImageDialog({ view: true, data: fileUrl, type: fileType });
      } else {
        dispatch(openLoaderWithMessage("Loading Please wait...."));
        downloadSingleFileAsync(fileUrl)
          .then((result) => {
            setImageDialog({
              view: true,
              data: result.data,
              type: result.type,
            });
          })
          .finally(() => {
            dispatch(closeLoaderWithMessage());
          });
      }
    }
  };

  const handlePreviewPdfFileInNewTab = async () => {
    if (file) {
      const downloadLink = URL.createObjectURL(file);
      window.open(downloadLink, "_blank");
      return;
    }
    if (!loading) {
      try {
        setLoading(true);
        await previewFile(props.value);
      } catch (err) {
        dispatch(
          showMessage({
            displayAs: "snackbar",
            message: "Someting went to be wrong!",
            type: "error",
          }),
        );
      } finally {
        setLoading(false);
      }
    }
  };
  return (
    <>
      <Stack
        direction="row"
        alignItems="center"
        borderRadius={2}
        position="relative"
        p={2}
        spacing={2}
        sx={{
          width: "100%",
          backgroundColor: "background.paper",
          boxShadow: 1,
          flexWrap: "wrap",

          border: "1px solid",
          borderColor: "divider",
          "&:hover": {
            boxShadow: 4,
            "& .file-actions": { opacity: 1 },
          },
          transition: "all 0.2s ease-in-out",
        }}
      >
        {/* File Type Icon */}
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 1,
            backgroundColor: "primary.light",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {fileIconMap[extensionType]}
        </Box>

        {/* File Name */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="subtitle1"
            noWrap
            sx={{ fontWeight: 600, whiteSpace: "unset" }}
            title={file ? file.name : value ? getFileNameFromUrl(value) : ""}
          >
            {file ? file.name : value ? getFileNameFromUrl(value) : ""}
          </Typography>
        </Box>

        {/* Loading Indicator */}
        {loading && (
          <LinearProgress
            color="primary"
            sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              height: 2,
              borderBottomLeftRadius: 8,
              borderBottomRightRadius: 8,
            }}
          />
        )}

        {/* Action Buttons */}
        <Box
          className="file-actions"
          sx={{
            display: "flex",
            gap: 1,
            opacity: 0,
            transition: "opacity 0.2s",
            ml: 1,
          }}
        >
          <Tooltip title="Preview">
            <span>
              <IconButton
                size="small"
                onClick={
                  openPreviewInNewTab
                    ? handlePreviewPdfFileInNewTab
                    : handlePreviewFile
                }
                disabled={loading}
              >
                <VisibilityOutlined fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Download">
            <span>
              <IconButton
                size="small"
                onClick={handleDownloadFile}
                disabled={loading}
              >
                <Download fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Delete">
            <span>
              <IconButton
                size="small"
                onClick={onDelete}
                disabled={deleteDisabled || loading}
              >
                <DeleteOutlined fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Stack>

      {imageDialog.view && (
        <ViewImageDialog
          open={imageDialog.view}
          file={imageDialog.data}
          fileType={imageDialog.type}
          orgImageUrl={value || ""}
          onClose={() =>
            setImageDialog({ view: false, data: "", type: "OTHER" })
          }
          onOpenInNewTab={handlePreviewPdfFileInNewTab}
        />
      )}
    </>
  );
};
